import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Search, ArrowLeft, Sparkles, Loader2, Zap, ShieldCheck, Code2,
  FileText, Tags, Database, KeyRound, Wand2, CheckCircle2, AlertTriangle,
  XCircle, ExternalLink, Copy, Check, Brain, LogIn, Lightbulb, ChevronDown,
  Download, Mail, History, GitCompare, Globe, Twitter, Image as ImageIcon, X,
  Crown
} from "lucide-react";
import { getStoredUser, getAuthConfig, loginWithGoogle } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: "easeOut" }
  })
};

const TABS = [
  { id: "audit",    label: "SEO Audit",         icon: ShieldCheck, blurb: "40+ on-page checks · SERP & social previews · PDF report" },
  { id: "meta",     label: "Meta Tags",         icon: Tags,        blurb: "5 click-optimized variants" },
  { id: "schema",   label: "Schema Generator",  icon: Database,    blurb: "JSON-LD for rich results" },
  { id: "keywords", label: "Keyword Clusters",  icon: KeyRound,    blurb: "Long-tail clusters by intent" },
  { id: "optimize", label: "Content Optimizer", icon: Wand2,       blurb: "Rewrite drafts for SEO" },
];

const SCHEMA_TYPES = ["Article","Product","FAQPage","LocalBusiness","HowTo","Recipe","Event","Course","Organization","Review","VideoObject","BreadcrumbList"];

/* ─── Status pill ─── */
function StatusPill({ status }) {
  const map = {
    pass: { bg: "bg-emerald-500/15", border: "border-emerald-500/30", color: "text-emerald-400", Icon: CheckCircle2, label: "Pass" },
    warn: { bg: "bg-amber-500/15",  border: "border-amber-500/30",  color: "text-amber-400",  Icon: AlertTriangle, label: "Warn" },
    fail: { bg: "bg-red-500/15",    border: "border-red-500/30",    color: "text-red-400",    Icon: XCircle, label: "Fail" },
  };
  const m = map[status] || map.warn;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${m.bg} ${m.border} ${m.color}`}>
      <m.Icon size={10} /> {m.label}
    </span>
  );
}

/* ─── Score Ring ─── */
function ScoreRing({ score, grade, size = 132 }) {
  const r = (size - 14) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? "#10b981" : score >= 60 ? "#00F0FF" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#27272A" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.2s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-white font-bold text-3xl leading-none">{score}</span>
        <span className="text-zinc-400 text-[11px] mt-1 uppercase tracking-wider">Grade {grade}</span>
      </div>
    </div>
  );
}

/* ─── Copy button ─── */
function CopyBtn({ text, label = "Copy", testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 transition-colors"
      data-testid={testid}
    >
      {done ? <><Check size={12} className="text-emerald-400" /> Copied</> : <><Copy size={12} /> {label}</>}
    </button>
  );
}

/* ─── AI Fix card (per check) ─── */
function AiFix({ url, check, testid }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  async function fetchFix() {
    if (data || loading) { setOpen(!open); return; }
    setOpen(true); setLoading(true); setErr("");
    try {
      const res = await axios.post(`${API}/seo/audit/suggest`, { url, check });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't generate a fix. Try again.");
    } finally { setLoading(false); }
  }

  const impactColor = {
    high: "text-red-400",
    medium: "text-amber-400",
    low: "text-zinc-400",
  };

  return (
    <div className="mt-2 pt-2 border-t border-zinc-800/50">
      <button
        onClick={fetchFix}
        className="inline-flex items-center gap-1.5 text-[11px] text-[#00F0FF] hover:text-[#0070FF] font-medium transition-colors"
        data-testid={testid}
      >
        {loading ? <Loader2 size={11} className="animate-spin" /> : <Lightbulb size={11} />}
        {data ? (open ? "Hide AI fix" : "Show AI fix") : (loading ? "Thinking..." : "Get AI fix")}
        {data && <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-2 space-y-2 overflow-hidden">
          {loading && <p className="text-zinc-500 text-xs">Asking Claude for a tailored fix...</p>}
          {err && <p className="text-amber-400 text-xs">{err}</p>}
          {data && (
            <>
              <div className="flex items-start justify-between gap-2">
                <p className="text-zinc-300 text-xs leading-relaxed flex-1">{data.summary}</p>
                {data.impact && (
                  <span className={`text-[9px] uppercase tracking-wider font-bold ${impactColor[data.impact] || "text-zinc-500"}`}>
                    {data.impact} impact
                  </span>
                )}
              </div>
              {data.steps?.length > 0 && (
                <ol className="text-zinc-300 text-xs space-y-1 list-decimal list-inside pl-1">
                  {data.steps.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
                </ol>
              )}
              {data.snippet && (
                <div className="bg-zinc-950 rounded-lg p-2.5 border border-zinc-800 relative">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">Code snippet</span>
                    <CopyBtn text={data.snippet} testid={`${testid}-copy`} />
                  </div>
                  <pre className="text-emerald-300 text-[11px] font-mono whitespace-pre-wrap leading-relaxed">{data.snippet}</pre>
                </div>
              )}
            </>
          )}
        </motion.div>
      )}
    </div>
  );
}

/* ─── SERP Preview ─── */
function SerpPreview({ data }) {
  const sp = data?.serp_preview || {};
  const titleLen = (sp.title || "").length;
  const descLen = (sp.description || "").length;
  const truncTitle = titleLen > 60 ? sp.title.slice(0, 60) + "…" : sp.title;
  const truncDesc = descLen > 160 ? sp.description.slice(0, 160) + "…" : sp.description;
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-5" data-testid="serp-preview">
      <div className="flex items-center gap-2 mb-3">
        <Globe size={14} className="text-[#00F0FF]" />
        <p className="text-zinc-300 text-sm font-medium">Google SERP Preview</p>
        <span className="text-zinc-600 text-[10px] ml-auto">title {titleLen} chars · desc {descLen} chars</span>
      </div>
      <div className="bg-white rounded-lg p-4 font-sans">
        <p className="text-[12px] text-[#202124]" style={{ fontFamily: "Arial,sans-serif" }}>{sp.url}</p>
        <p className="text-[18px] text-[#1a0dab] hover:underline cursor-pointer mt-1 leading-tight"
           style={{ fontFamily: "Arial,sans-serif" }}>{truncTitle || "(no title)"}</p>
        <p className="text-[13px] text-[#4d5156] mt-1.5 leading-snug" style={{ fontFamily: "Arial,sans-serif" }}>
          {truncDesc || "(no meta description)"}
        </p>
      </div>
    </div>
  );
}

/* ─── Social previews (OG + Twitter) ─── */
function SocialPreviews({ data }) {
  const og = data?.og_preview || {};
  const tw = data?.twitter_preview || {};
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="social-previews">
      {/* OG / Facebook / LinkedIn */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <ImageIcon size={14} className="text-[#00F0FF]" />
          <p className="text-zinc-300 text-sm font-medium">Open Graph (Facebook · LinkedIn)</p>
        </div>
        <div className="bg-[#f0f2f5] rounded-lg overflow-hidden border border-zinc-300">
          {og.image ? (
            <img src={og.image} alt="" className="w-full h-40 object-cover bg-zinc-200"
                 onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-full h-40 flex items-center justify-center bg-zinc-300 text-zinc-500 text-xs">No og:image</div>
          )}
          <div className="p-3 bg-white">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider truncate">{og.site_name}</p>
            <p className="text-[14px] text-zinc-900 font-semibold mt-1 leading-tight line-clamp-2">{og.title || "(no og:title)"}</p>
            <p className="text-[12px] text-zinc-600 mt-1 line-clamp-2">{og.description || "(no og:description)"}</p>
          </div>
        </div>
      </div>
      {/* Twitter */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Twitter size={14} className="text-[#00F0FF]" />
          <p className="text-zinc-300 text-sm font-medium">Twitter / X Card</p>
          <span className="text-zinc-600 text-[10px] ml-auto">{tw.card}</span>
        </div>
        <div className="bg-black rounded-2xl overflow-hidden border border-zinc-700">
          {tw.image && tw.card === "summary_large_image" ? (
            <img src={tw.image} alt="" className="w-full h-40 object-cover bg-zinc-800"
                 onError={(e) => { e.target.style.display = "none"; }} />
          ) : tw.image ? (
            <div className="flex items-center gap-3 p-3">
              <img src={tw.image} alt="" className="w-20 h-20 object-cover rounded-lg bg-zinc-800"
                   onError={(e) => { e.target.style.display = "none"; }} />
              <div>
                <p className="text-[14px] text-white font-semibold leading-tight line-clamp-2">{tw.title || "(no title)"}</p>
                <p className="text-[12px] text-zinc-400 mt-1 line-clamp-2">{tw.description}</p>
              </div>
            </div>
          ) : (
            <div className="w-full h-40 flex items-center justify-center bg-zinc-900 text-zinc-500 text-xs">No twitter:image</div>
          )}
          {tw.card === "summary_large_image" && (
            <div className="p-3 bg-black">
              <p className="text-[14px] text-white font-semibold leading-tight line-clamp-2">{tw.title || "(no title)"}</p>
              <p className="text-[12px] text-zinc-400 mt-1 line-clamp-2">{tw.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Category score bars ─── */
function CategoryScores({ scores, activeCat, onPick }) {
  const order = ["common", "advanced", "mobile", "speed", "security"];
  const entries = order.filter(k => scores[k]).map(k => [k, scores[k]]);
  const colorFor = (s) => s >= 80 ? "bg-emerald-500" : s >= 60 ? "bg-[#00F0FF]" : s >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-5" data-testid="category-scores">
      <p className="text-zinc-300 text-sm font-medium mb-3">Category breakdown</p>
      <div className="space-y-2.5">
        {entries.map(([cat, info]) => (
          <button key={cat} onClick={() => onPick(cat)}
            className={`w-full flex items-center gap-3 group ${activeCat === cat ? "opacity-100" : "opacity-90 hover:opacity-100"}`}
            data-testid={`cat-bar-${cat}`}>
            <span className={`text-xs font-semibold w-20 text-left capitalize ${activeCat === cat ? "text-[#00F0FF]" : "text-zinc-300"}`}>{cat}</span>
            <span className="text-zinc-500 text-[10px] w-32">{info.passed}p · {info.warnings}w · {info.failed}f</span>
            <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${info.score}%` }} transition={{ duration: 0.8 }}
                className={`h-full ${colorFor(info.score)}`} />
            </div>
            <span className="text-zinc-200 text-xs font-bold w-10 text-right">{info.score}%</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── To-Do list ─── */
function TodoList({ todo, onJump }) {
  if (!todo?.length) return null;
  const fails = todo.filter(t => t.status === "fail");
  const warns = todo.filter(t => t.status === "warn");
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-5" data-testid="todo-list">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb size={14} className="text-amber-400" />
        <p className="text-zinc-300 text-sm font-medium">To-Do List</p>
        <span className="text-zinc-600 text-[11px] ml-auto">{fails.length} fails · {warns.length} warnings</span>
      </div>
      <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
        {todo.slice(0, 12).map((t, i) => (
          <button key={i} onClick={() => onJump(t.id)}
            className="w-full flex items-center gap-2 text-left p-2 rounded-lg hover:bg-zinc-800/60 transition-colors"
            data-testid={`todo-${t.id}`}>
            {t.status === "fail"
              ? <XCircle size={12} className="text-red-400 shrink-0" />
              : <AlertTriangle size={12} className="text-amber-400 shrink-0" />}
            <span className="text-white text-xs font-medium">{t.name}</span>
            <span className="text-zinc-500 text-[10px] truncate flex-1">{t.msg}</span>
            <span className="text-zinc-600 text-[9px] uppercase tracking-wider">{t.category}</span>
          </button>
        ))}
        {todo.length > 12 && (
          <p className="text-zinc-600 text-[10px] text-center pt-2">+ {todo.length - 12} more issues below</p>
        )}
      </div>
    </div>
  );
}

/* ─── HTML head viewer ─── */
function HeadViewer({ html }) {
  const [open, setOpen] = useState(false);
  if (!html) return null;
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl" data-testid="head-viewer">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 text-sm">
        <span className="flex items-center gap-2 text-zinc-300"><Code2 size={14} className="text-[#00F0FF]" /> View raw HTML &lt;head&gt; ({Math.round(html.length/1024 * 10)/10} KB)</span>
        <ChevronDown size={14} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-zinc-800/60 p-3">
          <div className="flex justify-end mb-2">
            <CopyBtn text={html} testid="head-copy" />
          </div>
          <pre className="bg-zinc-950 rounded-lg p-3 text-[11px] text-emerald-300 font-mono overflow-x-auto max-h-80 whitespace-pre-wrap">{html}</pre>
        </div>
      )}
    </div>
  );
}

/* ─── Action toolbar ─── */
function ActionToolbar({ data, user, onCompare, onHistory }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailDone, setEmailDone] = useState(false);

  async function downloadPdf() {
    setPdfBusy(true);
    try {
      const res = await axios.post(`${API}/seo/audit/pdf`, { audit: data });
      const a = document.createElement("a");
      a.href = res.data.data_url;
      a.download = res.data.filename || "shansoai-seo-audit.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error(e);
    } finally { setPdfBusy(false); }
  }

  async function emailReport() {
    if (!user) { loginWithGoogle(); return; }
    setEmailBusy(true); setEmailDone(false);
    try {
      await axios.post(`${API}/seo/audit/email`, { audit: data }, getAuthConfig());
      setEmailDone(true);
      setTimeout(() => setEmailDone(false), 3500);
    } catch (e) {
      console.error(e);
    } finally { setEmailBusy(false); }
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid="action-toolbar">
      <button onClick={downloadPdf} disabled={pdfBusy}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-xs font-medium border border-zinc-800 disabled:opacity-50"
        data-testid="action-pdf">
        {pdfBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
        Download PDF
      </button>
      <button onClick={emailReport} disabled={emailBusy}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-xs font-medium border border-zinc-800 disabled:opacity-50"
        data-testid="action-email">
        {emailBusy ? <Loader2 size={13} className="animate-spin" /> : emailDone ? <Check size={13} className="text-emerald-400" /> : <Mail size={13} />}
        {emailDone ? "Sent!" : (user ? "Email me the report" : "Sign in to email report")}
      </button>
      {user && (
        <button onClick={onHistory}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-xs font-medium border border-zinc-800"
          data-testid="action-history">
          <History size={13} /> Past audits
        </button>
      )}
      <button onClick={onCompare}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-xs font-medium border border-zinc-800"
        data-testid="action-compare">
        <GitCompare size={13} /> Compare with another URL
      </button>
    </div>
  );
}

/* ─── Compare Modal ─── */
function CompareModal({ open, onClose, defaultA }) {
  const [urlA, setUrlA] = useState(defaultA || "");
  const [urlB, setUrlB] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  async function run() {
    if (!urlA.trim() || !urlB.trim()) { setErr("Both URLs required"); return; }
    setErr(""); setLoading(true); setData(null);
    try {
      const res = await axios.post(`${API}/seo/audit/compare`, {
        url_a: urlA.trim(), url_b: urlB.trim()
      }, getAuthConfig());
      setData(res.data);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (typeof det === "object" && det?.code === "login_required") {
        setErr(det.message);
      } else {
        setErr(typeof det === "string" ? det : (det?.message || "Compare failed."));
      }
    } finally { setLoading(false); }
  }

  if (!open) return null;
  const colorFor = (s) => s >= 80 ? "text-emerald-400" : s >= 60 ? "text-[#00F0FF]" : s >= 40 ? "text-amber-400" : "text-red-400";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl w-full max-w-4xl my-10 overflow-hidden" data-testid="compare-modal">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <p className="text-white font-semibold flex items-center gap-2"><GitCompare size={16} className="text-[#00F0FF]" /> Compare two URLs</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="compare-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input value={urlA} onChange={(e)=>setUrlA(e.target.value)} placeholder="https://your-site.com"
            className="w-full bg-zinc-900 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-zinc-600 outline-none"
            data-testid="compare-url-a" />
          <input value={urlB} onChange={(e)=>setUrlB(e.target.value)} placeholder="https://competitor.com"
            className="w-full bg-zinc-900 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-zinc-600 outline-none"
            data-testid="compare-url-b" />
          <button onClick={run} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#00F0FF] to-[#0070FF] text-black font-semibold text-sm disabled:opacity-50"
            data-testid="compare-run">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <GitCompare size={14} />}
            {loading ? "Auditing both..." : "Run side-by-side audit (uses 2 audits)"}
          </button>
          {err && <p className="text-amber-400 text-xs" data-testid="compare-error">{err}</p>}
        </div>
        {data && (
          <div className="p-5 grid md:grid-cols-2 gap-4 border-t border-zinc-800">
            {[["a", data.a], ["b", data.b]].map(([key, audit]) => (
              <div key={key} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 space-y-2" data-testid={`compare-result-${key}`}>
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider truncate">{audit.url}</p>
                <p className={`text-5xl font-bold ${colorFor(audit.score)}`}>{audit.score}<span className="text-zinc-500 text-base font-normal"> /100</span></p>
                <p className="text-zinc-400 text-xs">Grade {audit.grade}</p>
                <div className="flex gap-3 text-xs pt-2 border-t border-zinc-800">
                  <span className="text-emerald-400">{audit.summary?.passed} pass</span>
                  <span className="text-amber-400">{audit.summary?.warnings} warn</span>
                  <span className="text-red-400">{audit.summary?.failed} fail</span>
                </div>
                {audit.category_scores && (
                  <div className="space-y-1.5 pt-2">
                    {Object.entries(audit.category_scores).map(([cat, info]) => (
                      <div key={cat} className="flex items-center gap-2">
                        <span className="text-zinc-400 text-[10px] w-16 capitalize">{cat}</span>
                        <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                          <div className={`h-full ${info.score >= 80 ? "bg-emerald-500" : info.score >= 60 ? "bg-[#00F0FF]" : info.score >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${info.score}%` }} />
                        </div>
                        <span className="text-zinc-300 text-[10px] w-8 text-right">{info.score}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── History modal ─── */
function HistoryModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true); setErr("");
    try {
      const res = await axios.get(`${API}/seo/audit/history`, getAuthConfig());
      setItems(res.data.audits || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load history.");
    } finally { setLoading(false); }
  }

  // Lazy load on open
  if (open && !loading && items.length === 0 && !err) {
    load();
  }

  if (!open) return null;
  const colorFor = (s) => s >= 80 ? "text-emerald-400" : s >= 60 ? "text-[#00F0FF]" : s >= 40 ? "text-amber-400" : "text-red-400";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl w-full max-w-3xl my-10 overflow-hidden" data-testid="history-modal">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <p className="text-white font-semibold flex items-center gap-2"><History size={16} className="text-[#00F0FF]" /> Past audits</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="history-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2 max-h-[70vh] overflow-y-auto">
          {loading && <p className="text-zinc-500 text-sm">Loading...</p>}
          {err && <p className="text-amber-400 text-xs">{err}</p>}
          {!loading && !err && items.length === 0 && <p className="text-zinc-500 text-sm">No past audits yet.</p>}
          {items.map((it, i) => (
            <div key={i} className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 flex items-center gap-3" data-testid={`history-item-${i}`}>
              <span className={`text-2xl font-bold w-12 text-center ${colorFor(it.score)}`}>{it.score}</span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{it.serp_preview?.title || it.url}</p>
                <p className="text-zinc-500 text-xs truncate">{it.url}</p>
              </div>
              <p className="text-zinc-600 text-[11px] shrink-0">{new Date(it.audited_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── AUDIT TAB ─── */
function AuditTab({ state, set }) {
  const { url, loading, err, data, needLogin, needUpgrade, activeCat } = state;
  const [compareOpen, setCompareOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const user = getStoredUser();

  async function run() {
    if (!url.trim()) { set({ err: "Enter a URL" }); return; }
    set({ err: "", loading: true, data: null });
    try {
      const res = await axios.post(`${API}/seo/audit`, { url: url.trim() }, getAuthConfig());
      set({ data: res.data, needLogin: false, needUpgrade: false, loading: false });
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "rate_limited") {
        set({
          needLogin: !!det.requires_login,
          needUpgrade: !!det.requires_upgrade,
          currentPlan: det.current_plan,
          err: det.message || "Daily free limit reached.",
          loading: false,
        });
      } else {
        set({ err: typeof det === "string" ? det : "Audit failed. Try a public URL.", loading: false });
      }
    }
  }

  function jumpTo(checkId) {
    const el = document.getElementById(`check-${checkId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#00F0FF]");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#00F0FF]"), 1800);
    }
  }

  const cats = data?.categories || {};
  const catKeys = Object.keys(cats);
  const visibleChecks = (() => {
    if (!data) return [];
    if (activeCat === "all") return data.checks;
    return cats[activeCat] || [];
  })();

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/40 rounded-2xl p-5 border border-zinc-800/60">
        <div className="grid sm:grid-cols-[1fr,auto] gap-3">
          <input
            type="url"
            value={url}
            onChange={(e) => set({ url: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="https://your-site.com"
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none"
            data-testid="audit-url-input"
          />
          <button
            onClick={run}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#00F0FF] to-[#0070FF] hover:opacity-90 text-black font-semibold text-sm disabled:opacity-50"
            data-testid="audit-run-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {loading ? "Auditing..." : "Run Audit"}
          </button>
        </div>

        {err && !needLogin && !needUpgrade && <p className="text-amber-400 text-xs mt-3" data-testid="audit-error">{err}</p>}

        {needUpgrade && user && (
          <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-[#FF8AB8]/10 to-[#FF3DA0]/10 border border-[#FF8AB8]/30" data-testid="audit-upgrade-card">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#FF8AB8]/20 flex items-center justify-center">
                <Crown size={16} className="text-[#FF8AB8]" />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-semibold">Monthly limit reached on Free plan</p>
                <p className="text-zinc-300 text-xs mt-1 leading-relaxed">
                  You've used all your audits for today. Upgrade to <span className="text-[#FF8AB8] font-semibold">SEO Starter (₹999/mo)</span> for 100 audits/mo, or <span className="text-[#FF8AB8] font-semibold">Pro (₹1,999/mo)</span> for unlimited.
                </p>
                <Link
                  to="/pricing#seo"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#FF8AB8] to-[#FF3DA0] text-black font-semibold text-sm transition-opacity hover:opacity-90"
                  data-testid="audit-upgrade-btn"
                >
                  <Crown size={14} /> See SEO plans
                </Link>
              </div>
            </div>
          </div>
        )}

        {needLogin && !user && (
          <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-[#00F0FF]/10 to-[#0070FF]/10 border border-[#00F0FF]/30">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
                <LogIn size={16} className="text-[#00F0FF]" />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-semibold">Daily free limit reached</p>
                <p className="text-zinc-300 text-xs mt-1 leading-relaxed">
                  Sign in with Google to unlock <span className="text-[#00F0FF] font-semibold">20 audits/day</span>, plus full access to Meta Tags, Schema, Keywords, and Content Optimizer.
                </p>
                <button
                  onClick={() => loginWithGoogle()}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white hover:bg-zinc-100 text-zinc-900 font-semibold text-sm transition-colors"
                  data-testid="audit-login-btn"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Sign in with Google
                </button>
              </div>
            </div>
          </div>
        )}

        <p className="text-zinc-500 text-[11px] mt-3">
          {user
            ? <>Signed in as <span className="text-zinc-300">{user.email}</span> · 20 audits/day · Pro: unlimited.</>
            : <>Free tier: 1 audit/day per IP · Sign in for 20/day · Pro: unlimited.</>}
        </p>
      </div>

      {data && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="audit-result">
          {/* Score panel */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800/60 rounded-2xl p-6">
            <div className="flex flex-wrap items-center gap-6">
              <ScoreRing score={data.score} grade={data.grade} />
              <div className="flex-1 min-w-[240px] space-y-3">
                <div>
                  <p className="text-zinc-500 text-[11px] uppercase tracking-wider">Audited URL</p>
                  <a href={data.url} target="_blank" rel="noreferrer" className="text-white font-medium text-sm hover:text-[#00F0FF] inline-flex items-center gap-1 break-all">
                    {data.url} <ExternalLink size={12} />
                  </a>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                    <p className="text-emerald-400 text-2xl font-bold">{data.summary.passed}</p>
                    <p className="text-emerald-300/80 text-[10px] uppercase tracking-wider">Passed</p>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    <p className="text-amber-400 text-2xl font-bold">{data.summary.warnings}</p>
                    <p className="text-amber-300/80 text-[10px] uppercase tracking-wider">Warnings</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                    <p className="text-red-400 text-2xl font-bold">{data.summary.failed}</p>
                    <p className="text-red-300/80 text-[10px] uppercase tracking-wider">Failed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action toolbar */}
          <ActionToolbar data={data} user={user}
            onCompare={() => setCompareOpen(true)}
            onHistory={() => setHistoryOpen(true)} />

          {/* Category breakdown */}
          {data.category_scores && (
            <CategoryScores scores={data.category_scores} activeCat={activeCat}
              onPick={(c) => set({ activeCat: activeCat === c ? "all" : c })} />
          )}

          {/* SERP Preview */}
          <SerpPreview data={data} />

          {/* Social previews */}
          <SocialPreviews data={data} />

          {/* HTML head viewer */}
          <HeadViewer html={data.head_html} />

          {/* To-Do List */}
          <TodoList todo={data.todo} onJump={jumpTo} />

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={() => set({ activeCat: "all" })}
              className={`text-[11px] px-3 py-1.5 rounded-full border ${activeCat==="all" ? "bg-[#00F0FF]/15 border-[#00F0FF]/40 text-[#00F0FF]" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
              data-testid="cat-all">All ({data.checks.length})</button>
            {catKeys.map(k => (
              <button key={k} onClick={() => set({ activeCat: k })}
                className={`text-[11px] px-3 py-1.5 rounded-full border capitalize ${activeCat===k ? "bg-[#00F0FF]/15 border-[#00F0FF]/40 text-[#00F0FF]" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
                data-testid={`cat-${k}`}>{k} ({cats[k].length})</button>
            ))}
          </div>

          {/* Checks */}
          <div className="grid md:grid-cols-2 gap-2">
            {visibleChecks.map((c, i) => (
              <div id={`check-${c.id}`} key={c.id || i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3 space-y-1.5 transition-shadow" data-testid={`check-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-white text-sm font-medium">{c.name}</p>
                  <StatusPill status={c.status} />
                </div>
                <p className="text-zinc-400 text-xs leading-relaxed">{c.msg}</p>
                {(c.status === "warn" || c.status === "fail") && (
                  <AiFix url={data.url} check={c} testid={`fix-${c.id}`} />
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <CompareModal open={compareOpen} onClose={() => setCompareOpen(false)} defaultA={data?.url || url} />
      <HistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
}

/* ─── META TAB ─── */
function MetaTab({ state, set }) {
  const { topic, keyword, audience, loading, err, data } = state;

  async function run() {
    if (!topic.trim() || !keyword.trim()) { set({ err: "Topic and primary keyword are required" }); return; }
    set({ err: "", loading: true, data: null });
    try {
      const res = await axios.post(`${API}/seo/meta-tags`, {
        page_topic: topic.trim(),
        primary_keyword: keyword.trim(),
        audience: audience.trim() || "general visitors",
      }, getAuthConfig());
      set({ data: res.data, loading: false });
    } catch (e) {
      set({ err: e?.response?.data?.detail || "Generation failed.", loading: false });
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/40 rounded-2xl p-5 border border-zinc-800/60 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <input type="text" value={topic} onChange={(e)=>set({ topic: e.target.value })} placeholder="Page topic (e.g. AI training for founders)"
            className="bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none"
            data-testid="meta-topic-input" />
          <input type="text" value={keyword} onChange={(e)=>set({ keyword: e.target.value })} placeholder="Primary keyword (e.g. AI for founders)"
            className="bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none"
            data-testid="meta-keyword-input" />
        </div>
        <input type="text" value={audience} onChange={(e)=>set({ audience: e.target.value })} placeholder="Audience (optional, e.g. B2B SaaS founders)"
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none"
          data-testid="meta-audience-input" />
        <button onClick={run} disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#00F0FF] to-[#0070FF] hover:opacity-90 text-black font-semibold text-sm disabled:opacity-50"
          data-testid="meta-run-btn">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {loading ? "Crafting..." : "Generate 5 Variants"}
        </button>
        {err && <p className="text-amber-400 text-xs" data-testid="meta-error">{err}</p>}
      </div>

      {data?.variants?.length > 0 && (
        <div className="space-y-3" data-testid="meta-result">
          {data.variants.map((v, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 space-y-2.5">
              <div className="flex items-start justify-between gap-3">
                <span className="text-[10px] uppercase tracking-wider text-[#00F0FF] font-bold">Variant {i+1}</span>
                <CopyBtn testid={`meta-copy-${i}`} text={`<title>${v.title}</title>\n<meta name="description" content="${v.description}">\n<meta property="og:title" content="${v.og_title}">\n<meta property="og:description" content="${v.og_description}">\n<meta name="twitter:title" content="${v.twitter_title}">\n<meta name="twitter:description" content="${v.twitter_description}">`} />
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Title ({v.title?.length || 0} chars)</p>
                <p className="text-white text-sm font-medium">{v.title}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Meta description ({v.description?.length || 0} chars)</p>
                <p className="text-zinc-300 text-sm">{v.description}</p>
              </div>
              {v.reasoning && <p className="text-[#00F0FF]/80 text-xs italic">{v.reasoning}</p>}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── SCHEMA TAB ─── */
function SchemaTab({ state, set }) {
  const { type, pageUrl, details, loading, err, data } = state;

  async function run() {
    set({ err: "", loading: true, data: null });
    let parsed;
    try { parsed = JSON.parse(details || "{}"); }
    catch { set({ err: "Details must be valid JSON", loading: false }); return; }
    try {
      const res = await axios.post(`${API}/seo/schema`, {
        schema_type: type,
        page_url: pageUrl.trim() || null,
        details: parsed,
      }, getAuthConfig());
      set({ data: res.data, loading: false });
    } catch (e) {
      set({ err: typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Generation failed.", loading: false });
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/40 rounded-2xl p-5 border border-zinc-800/60 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <select value={type} onChange={(e)=>set({ type: e.target.value })}
            className="bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white outline-none"
            data-testid="schema-type-select">
            {SCHEMA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="url" value={pageUrl} onChange={(e)=>set({ pageUrl: e.target.value })} placeholder="Page URL (optional)"
            className="bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none"
            data-testid="schema-url-input" />
        </div>
        <div>
          <p className="text-zinc-500 text-[11px] uppercase tracking-wider mb-1.5">Details (JSON — describe the entity)</p>
          <textarea value={details} onChange={(e)=>set({ details: e.target.value })} rows={8}
            className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white font-mono outline-none"
            data-testid="schema-details-input" />
        </div>
        <button onClick={run} disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#00F0FF] to-[#0070FF] hover:opacity-90 text-black font-semibold text-sm disabled:opacity-50"
          data-testid="schema-run-btn">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Code2 size={16} />}
          {loading ? "Generating..." : "Generate JSON-LD"}
        </button>
        {err && <p className="text-amber-400 text-xs" data-testid="schema-error">{err}</p>}
      </div>

      {data?.jsonld && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="schema-result">
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-zinc-300 text-sm font-medium">JSON-LD Output</p>
              <CopyBtn testid="schema-copy" label="Copy <script>" text={`<script type="application/ld+json">\n${JSON.stringify(data.jsonld, null, 2)}\n</script>`} />
            </div>
            <pre className="bg-zinc-950 rounded-lg p-3 text-xs text-emerald-300 font-mono overflow-x-auto max-h-80">{JSON.stringify(data.jsonld, null, 2)}</pre>
          </div>
          {data.validation_notes && (
            <div className="bg-[#00F0FF]/5 border border-[#00F0FF]/20 rounded-xl p-3">
              <p className="text-[#00F0FF] text-[11px] uppercase tracking-wider font-bold mb-1">Validation Notes</p>
              <p className="text-zinc-300 text-xs leading-relaxed">{data.validation_notes}</p>
            </div>
          )}
          {data.recommended_additions?.length > 0 && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-3">
              <p className="text-zinc-400 text-[11px] uppercase tracking-wider font-bold mb-2">Recommended additions</p>
              <ul className="text-zinc-300 text-xs space-y-1 list-disc list-inside">
                {data.recommended_additions.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

/* ─── KEYWORDS TAB ─── */
function KeywordsTab({ state, set }) {
  const { seed, audience, n, loading, err, data } = state;

  async function run() {
    if (!seed.trim()) { set({ err: "Seed topic is required" }); return; }
    set({ err: "", loading: true, data: null });
    try {
      const res = await axios.post(`${API}/seo/keywords`, {
        seed_topic: seed.trim(),
        audience: audience.trim() || "general",
        n: Number(n),
      }, getAuthConfig());
      set({ data: res.data, loading: false });
    } catch (e) {
      set({ err: e?.response?.data?.detail || "Generation failed.", loading: false });
    }
  }

  const intentColors = {
    informational: "bg-[#00F0FF]/15 border-[#00F0FF]/30 text-[#00F0FF]",
    commercial: "bg-amber-500/15 border-amber-500/30 text-amber-400",
    transactional: "bg-emerald-500/15 border-emerald-500/30 text-emerald-400",
    navigational: "bg-purple-500/15 border-purple-500/30 text-purple-400",
  };
  const diffColors = { low: "text-emerald-400", medium: "text-amber-400", high: "text-red-400" };

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/40 rounded-2xl p-5 border border-zinc-800/60 space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <input type="text" value={seed} onChange={(e)=>set({ seed: e.target.value })} placeholder="Seed topic (e.g. AI training)"
            className="sm:col-span-2 bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none"
            data-testid="kw-seed-input" />
          <select value={n} onChange={(e)=>set({ n: e.target.value })}
            className="bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white outline-none"
            data-testid="kw-n-select">
            <option value={30}>30 keywords</option>
            <option value={50}>50 keywords</option>
            <option value={75}>75 keywords</option>
            <option value={100}>100 keywords</option>
          </select>
        </div>
        <input type="text" value={audience} onChange={(e)=>set({ audience: e.target.value })} placeholder="Audience (optional, e.g. founders)"
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none"
          data-testid="kw-audience-input" />
        <button onClick={run} disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#00F0FF] to-[#0070FF] hover:opacity-90 text-black font-semibold text-sm disabled:opacity-50"
          data-testid="kw-run-btn">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
          {loading ? "Researching..." : "Generate Keyword Map"}
        </button>
        {err && <p className="text-amber-400 text-xs" data-testid="kw-error">{err}</p>}
      </div>

      {data?.clusters?.length > 0 && (
        <div className="space-y-4" data-testid="kw-result">
          {data.clusters.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h4 className="text-white font-semibold text-sm">{c.theme}</h4>
                  <p className="text-zinc-500 text-xs mt-0.5">{c.keywords?.length || 0} keywords</p>
                </div>
                <span className={`text-[10px] px-2.5 py-1 rounded-full border uppercase tracking-wider font-bold ${intentColors[c.intent] || "bg-zinc-800 border-zinc-700 text-zinc-300"}`}>
                  {c.intent}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {c.keywords?.map((k, j) => (
                  <div key={j} className="bg-zinc-950/60 border border-zinc-800 rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-zinc-200 text-sm">{k.keyword}</span>
                      <span className={`text-[10px] uppercase font-bold ${diffColors[k.difficulty] || "text-zinc-500"}`}>{k.difficulty}</span>
                    </div>
                    {k.angle && <p className="text-zinc-500 text-[11px] mt-0.5">{k.angle}</p>}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── OPTIMIZE TAB ─── */
function OptimizeTab({ state, set }) {
  const { content, keyword, loading, err, data } = state;

  async function run() {
    if (content.trim().length < 100) { set({ err: "Paste at least 100 characters of content" }); return; }
    if (!keyword.trim()) { set({ err: "Primary keyword is required" }); return; }
    set({ err: "", loading: true, data: null });
    try {
      const res = await axios.post(`${API}/seo/optimize-content`, {
        content: content.trim(),
        primary_keyword: keyword.trim(),
      }, getAuthConfig());
      set({ data: res.data, loading: false });
    } catch (e) {
      set({ err: e?.response?.data?.detail || "Generation failed.", loading: false });
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/40 rounded-2xl p-5 border border-zinc-800/60 space-y-3">
        <input type="text" value={keyword} onChange={(e)=>set({ keyword: e.target.value })} placeholder="Primary keyword (e.g. python tutor)"
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none"
          data-testid="opt-keyword-input" />
        <textarea value={content} onChange={(e)=>set({ content: e.target.value })} rows={10}
          placeholder="Paste your draft article here (min 100 characters)..."
          className="w-full bg-zinc-950 border border-zinc-800 focus:border-[#00F0FF]/50 rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none font-mono"
          data-testid="opt-content-input" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-zinc-500 text-[11px]">{content.length} chars · {content.trim().split(/\s+/).filter(Boolean).length} words</p>
          <button onClick={run} disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#00F0FF] to-[#0070FF] hover:opacity-90 text-black font-semibold text-sm disabled:opacity-50"
            data-testid="opt-run-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
            {loading ? "Rewriting..." : "Optimize for SEO"}
          </button>
        </div>
        {err && <p className="text-amber-400 text-xs" data-testid="opt-error">{err}</p>}
      </div>

      {data && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="opt-result">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-3">
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Keyword density</p>
              <p className="text-[#00F0FF] text-2xl font-bold">{data.keyword_density_pct ?? "—"}%</p>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-3">
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Suggested meta title</p>
              <p className="text-white text-sm">{data.meta_title}</p>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-3">
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Suggested meta description</p>
              <p className="text-zinc-300 text-xs">{data.meta_description}</p>
            </div>
          </div>

          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-zinc-200 text-sm font-medium">Optimized Article (Markdown)</p>
              <CopyBtn testid="opt-copy-md" text={data.optimized_markdown || ""} />
            </div>
            <pre className="bg-zinc-950 rounded-lg p-3 text-xs text-zinc-200 whitespace-pre-wrap max-h-[480px] overflow-y-auto font-sans leading-relaxed">{data.optimized_markdown}</pre>
          </div>

          {data.changes?.length > 0 && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
              <p className="text-zinc-400 text-[11px] uppercase tracking-wider font-bold mb-2">What changed</p>
              <ul className="space-y-1.5 text-zinc-300 text-xs">
                {data.changes.map((c, i) => (
                  <li key={i} className="flex items-start gap-2"><Check size={12} className="text-emerald-400 mt-0.5 shrink-0" /> {c}</li>
                ))}
              </ul>
            </div>
          )}

          {data.faq_section?.length > 0 && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 space-y-3">
              <p className="text-zinc-400 text-[11px] uppercase tracking-wider font-bold">FAQ section (drop-in)</p>
              {data.faq_section.map((f, i) => (
                <div key={i} className="border-l-2 border-[#00F0FF]/40 pl-3">
                  <p className="text-white text-sm font-medium">{f.q}</p>
                  <p className="text-zinc-400 text-xs mt-1">{f.a}</p>
                </div>
              ))}
            </div>
          )}

          {data.recommended_internal_links?.length > 0 && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
              <p className="text-zinc-400 text-[11px] uppercase tracking-wider font-bold mb-2">Recommended internal anchors</p>
              <div className="flex flex-wrap gap-2">
                {data.recommended_internal_links.map((l, i) => (
                  <span key={i} className="text-[11px] px-2.5 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-300">{l}</span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

/* ─── PAGE — state lifted up so tabs persist their results ─── */
const INITIAL = {
  audit:    { url: "", loading: false, err: "", data: null, needLogin: false, activeCat: "all" },
  meta:     { topic: "", keyword: "", audience: "", loading: false, err: "", data: null },
  schema:   { type: "Article", pageUrl: "", details: '{\n  "title": "How to learn Python in 30 days",\n  "author": "Jane Doe",\n  "datePublished": "2026-02-10"\n}', loading: false, err: "", data: null },
  keywords: { seed: "", audience: "", n: 50, loading: false, err: "", data: null },
  optimize: { content: "", keyword: "", loading: false, err: "", data: null },
};

export default function SeoServicesPage() {
  const [active, setActive] = useState("audit");
  const [state, setState] = useState(INITIAL);

  const updater = (tab) => (patch) =>
    setState((prev) => ({ ...prev, [tab]: { ...prev[tab], ...patch } }));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-zinc-800/60 bg-zinc-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/ai-tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm" data-testid="back-to-tools">
            <ArrowLeft size={16} /> AI Tools
          </Link>
          <div className="flex items-center gap-2 text-[#00F0FF]">
            <Brain size={18} />
            <span className="font-semibold tracking-tight text-sm">SEO Suite</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-[11px] font-medium mb-4">
            <Zap size={11} /> Powered by Claude Sonnet 4.5
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Five SEO tools.<br />
            <span className="bg-gradient-to-r from-[#00F0FF] to-[#0070FF] bg-clip-text text-transparent">One workflow.</span>
          </h1>
          <p className="text-zinc-400 text-base mt-4 max-w-2xl">
            Audit any URL, generate meta tags, build JSON-LD schema, plan keyword clusters, and rewrite drafts for SEO — all in one place.
          </p>
        </motion.div>
      </section>

      {/* Tabs */}
      <section className="max-w-6xl mx-auto px-4 sticky top-[60px] bg-[#0a0a0a]/95 backdrop-blur z-20 border-b border-zinc-900">
        <div className="flex overflow-x-auto -mb-px">
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${isActive ? "text-[#00F0FF] border-[#00F0FF]" : "text-zinc-500 border-transparent hover:text-zinc-200"}`}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={15} />
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Active blurb */}
      <section className="max-w-6xl mx-auto px-4 pt-5">
        <p className="text-zinc-500 text-xs flex items-center gap-2">
          <FileText size={12} className="text-[#00F0FF]" /> {TABS.find(t => t.id === active)?.blurb}
        </p>
      </section>

      {/* Tab content — stays mounted via display toggle so results persist */}
      <main className="max-w-6xl mx-auto px-4 py-6 pb-20">
        <div style={{ display: active === "audit" ? "block" : "none" }}>
          <AuditTab state={state.audit} set={updater("audit")} />
        </div>
        <div style={{ display: active === "meta" ? "block" : "none" }}>
          <MetaTab state={state.meta} set={updater("meta")} />
        </div>
        <div style={{ display: active === "schema" ? "block" : "none" }}>
          <SchemaTab state={state.schema} set={updater("schema")} />
        </div>
        <div style={{ display: active === "keywords" ? "block" : "none" }}>
          <KeywordsTab state={state.keywords} set={updater("keywords")} />
        </div>
        <div style={{ display: active === "optimize" ? "block" : "none" }}>
          <OptimizeTab state={state.optimize} set={updater("optimize")} />
        </div>
      </main>
    </div>
  );
}
